import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "lucide-react";
import Table from "components/shared/tables/Table";
import ColumnToggle from "components/shared/tables/Table/ColumnToggle";
import Filters from "components/shared/filters/Filters";
import QuickFilters from "components/shared/filters/QuickFilters";
import DateFilters from "components/shared/filters/DateFilters";
import Button from "components/shared/Button";
import Widget, { STATISTICS_URL } from "./components/Widget";
import Menu, { useMenuTab } from "./components/Menu";
import { useTable } from "components/shared/tables/Table/hooks/useTable";
import { useFilterConfig } from "components/shared/filters/Filters/hooks/useFilterConfig";
import { useColumns } from "./hooks/useColumns";
import { useFormSheet } from "./hooks/useFromSheet";
import { useEditSheet } from "./hooks/useEditSheet";
import { useViewSheet } from "./hooks/useViewSheet";
import { useAssignDialog } from "./hooks/useAssignDialog";
import { useCancelDialog } from "./hooks/useCancelDialog";
import { useApproveDialog } from "../../Component/hooks/modals/useApproveDialog";
import { useDenyDialog } from "../../Component/hooks/modals/useDenyDialog";
import { useProfileSheet } from "components/shared/ProfileSheet/hooks/useProfileSheet";
import { useDeleteDialog } from "components/shared/Dialog/useDeleteDialog";
import useApi from "utils/api";
import { useFilters } from "utils/hooks/useFilters";
import { formatParams } from "utils/api/format";

const REQUEST_TYPES = ["open-shift", "call-off", "direct-offer"];

function Content() {
    const { t } = useTranslation();
    const { authGet, authDelete } = useApi();
    const [{ filters, from, to, page = 1, pageSize }] = useFilters();
    const [menu, setMenu] = useMenuTab();

    const filterParams = useMemo(() => ({
        ...formatParams({ pageIndex: undefined, pageSize: undefined, filters }),
        "filter[type][method]": "exact",
        "filter[type][value][]": REQUEST_TYPES,
        startDate: from,
        endDate: to,
        namespace: "schedule"
    }), [filters, from, to]);

    const params = useMemo(() => ({
        ...filterParams,
        coverageStatus: menu,
        page,
        perPage: pageSize,
    }), [filterParams, menu, page, pageSize]);

    const queryOptions = { queryKey: ["/request/open-shifts", params] };

    const { data, isFetching } = useQuery({
        ...queryOptions,
        queryFn: ({ queryKey, signal }: any) => {
            const [url, params] = queryKey;
            return authGet(url, { params, signal });
        },
    });

    const { open: openFormSheet, sheet: formSheet } = useFormSheet();
    const queryClient = useQueryClient();
    const refetchStatistics = () => queryClient.invalidateQueries({ queryKey: [STATISTICS_URL] });
    const { open: approve, dialog: approveDialog } = useApproveDialog({ queryOptions, onSuccess: refetchStatistics });
    const { open: deny, dialog: denyDialog } = useDenyDialog({ queryOptions, onSuccess: refetchStatistics });
    const { open: edit, sheet: editSheet } = useEditSheet({ queryOptions });
    const { open: view, sheet: viewSheet } = useViewSheet({ approve, deny });
    const { open: openProfile, sheet: profileSheet } = useProfileSheet();
    const { open: assign, dialog: assignDialog } = useAssignDialog({ queryOptions, onSuccess: refetchStatistics });
    const { open: cancel, dialog: cancelDialog } = useCancelDialog({ queryOptions, onSuccess: refetchStatistics });

    const { mutateAsync: deleteRequest, isPending: isDeleting } = useMutation({
        mutationFn: (request: any) => authDelete(`/request/${request.id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
            refetchStatistics();
        },
    });

    const { open: remove, dialog: deleteDialog } = useDeleteDialog({
        message: () => t("do-you-want-to-delete-request"),
        loading: isDeleting,
        onConfirm: deleteRequest,
    });

    const columns = useColumns({ approve, deny, cancel, edit, view, remove, assign, openProfile, menu });

    const table = useTable({
        data: data?.result,
        totalItems: data?.totalItems,
        loading: isFetching,
        columns,
        enableRowSelection: false,
        enableSorting: false,
        defaultColumnVisibility: {
            skills: false,
            licenses: false,
            certifications: false,
            takeUser: false,
            createdAt: false,
        },
    });

    const filterConfig = useFilterConfig(table.getAllColumns());

    return (
        <div className="tw-h-full tw-w-full tw-bg-white tw-rounded-lg tw-flex tw-flex-col">
            <h1 className="tw-p-5 tw-border-b tw-mb-3">{t("open-shifts-management")}</h1>
            <div className="tw-flex tw-justify-between tw-flex-wrap tw-mb-5 tw-px-5 tw-border-b tw-pb-3 gap-2">
                <Menu value={menu} onChange={setMenu} />
                <DateFilters />
            </div>

            <Widget params={filterParams} />

            <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-mb-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Filters config={filterConfig} />
                    <QuickFilters />
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Button
                        type="button"
                        icon={<PlusIcon />}
                        onClick={openFormSheet}
                    >
                        {t("create-open-shift")}
                    </Button>
                    <ColumnToggle columns={table.getAllColumns()} />
                </div>
            </div>

            <div className="tw-flex-1 tw-min-h-0 tw-px-5">
                <Table table={table} />
            </div>
            {formSheet}
            {viewSheet}
            {editSheet}
            {approveDialog}
            {denyDialog}
            {deleteDialog}
            {assignDialog}
            {cancelDialog}
            {profileSheet}
        </div>
    );
}

export default Content;
